/**
 * Sentence splitting, shared by the server (sentence cache keys, src/lib/sentenceCache.ts)
 * and the widget (browser-voice boundaries, translation batches). No imports, so the
 * widget bundle can include it. No regex lookbehind: the widget targets Safari 14.
 *
 * A period ends a sentence only when the next word could start one, and not after
 * abbreviations that usually continue it ("Dr. Lee", "Oct. 3", "p.m. on", "No. 7").
 */

/** Titles and place words followed by a name: "Dr. Lee", "Gen. Grant", "St. Louis" */
const TITLES = 'Mr|Mrs|Ms|Dr|Prof|Sr|Jr|St|Mt|Ft|Gen|Capt|Col|Lt|Sgt|Gov|Sen|Rep|Rev|Pres|Hon|Supt'
/** Abbreviations followed by a number: "Oct. 3", "No. 7", "p. 12" */
const BEFORE_NUMBER = 'Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec|No|Nos|Fig|Figs|Vol|Vols|p|pp|Ch|Sec|Ex|Eq|Art'

export function splitIntoSentences(text: string): string[] {
  const protectedText = text
    .replace(new RegExp(`\\b(${TITLES})\\.(?=\\s+[A-Z])`, 'g'), '$1<DOT>')
    .replace(new RegExp(`\\b(${BEFORE_NUMBER})\\.(?=\\s+\\d)`, 'g'), '$1<DOT>')
    .replace(/\b([A-Z])\.([A-Z])\./g, '$1<DOT>$2<DOT>') // U.S., U.K.
    .replace(/\b([A-Z])\.(?=\s+[A-Z])/g, '$1<DOT>') // initials: "J. K. Rowling"
    .replace(/\s(vs|etc|i\.e|e\.g)\./gi, ' $1<DOT>')
    // Anything else followed by a lowercase word continues the sentence ("p.m. on")
    .replace(/\.(?=\s+[a-z])/g, '<DOT>');

  // Split on whitespace that follows terminal punctuation, keeping the
  // punctuation attached (and leaving decimals like "3.5" intact)
  const sentences = protectedText
    .replace(/([.!?])\s+/g, '$1\u0000')
    .split('\u0000')
    .map(s => s.replace(/<DOT>/g, '.').trim())
    .filter(s => s.length > 0 && !/^[.!?]+$/.test(s));

  // Combine short fragments (< 10 chars) with the next sentence
  const result: string[] = [];
  let carry = '';
  for (const sentence of sentences) {
    const combined = carry ? `${carry} ${sentence}` : sentence;
    if (combined.length < 10) {
      carry = combined;
      continue;
    }
    result.push(combined);
    carry = '';
  }
  if (carry) result.push(carry);

  return result;
}
